import { Heart } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFavorites, useAddFavorite, useRemoveFavorite } from '@/hooks/useFavorites';
import { useLocale } from '@/contexts/LocaleContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface FavoriteButtonProps {
  activityId: string;
  className?: string;
  size?: 'sm' | 'md';
}

export function FavoriteButton({ activityId, className = '', size = 'md' }: FavoriteButtonProps) {
  const { user } = useAuth();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { data: favorites = [] } = useFavorites();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  const isFavorite = favorites.some((f) => f.activity_id === activityId);
  const isArabic = locale === 'ar';

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.error(isArabic ? 'سجّل الدخول لإضافة المفضلة' : 'Sign in to add favorites');
      navigate('/auth');
      return;
    }

    if (isFavorite) {
      removeFavorite.mutate(activityId);
    } else {
      addFavorite.mutate(activityId);
    }
  };

  const sizeClasses = size === 'sm' ? 'w-8 h-8' : 'w-9 h-9';
  const iconSize = size === 'sm' ? 'w-6 h-6' : 'w-7 h-7';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={addFavorite.isPending || removeFavorite.isPending}
      aria-label={isFavorite ? (isArabic ? 'إزالة من المفضلة' : 'Remove from favorites') : (isArabic ? 'إضافة إلى المفضلة' : 'Add to favorites')}
      className={`${sizeClasses} flex items-center justify-center transition-all hover:scale-110 disabled:opacity-50 ${className}`}
    >
      <Heart
        strokeWidth={2}
        className={`${iconSize} transition-colors drop-shadow-md ${
          isFavorite ? 'fill-red-500 text-white' : 'fill-foreground/40 text-white'
        }`}
      />
    </button>
  );
}
